import classNames from 'classnames'
import React from 'react'

import type { LinkAttachmentCta } from '../../types'

import CardCta from './CardCta'
import type { LinkAttachmentVariant } from './CardShell'

export interface CardBodyProps {
  variant: LinkAttachmentVariant
  /**
   * Surface the CTA button paints against. Defaults to `variant`; diverges only
   * for the audio strip (a light surface under a nominally `dark` card), so a
   * `secondary` CTA stays legible — matching how `chinTextColor` adapts the chin
   * text. Sourced from `useChinPalette().surface`.
   */
  ctaSurface?: LinkAttachmentVariant
  title?: string
  /** Placeholder shown in the title slot when no title is set (dark variants only). */
  placeholderTitle?: string
  description?: string
  /**
   * Max lines the description renders before truncating with an ellipsis.
   * Defaults to `1` (single-line truncate — the link-preview default). Set
   * higher for cards whose body is prose (e.g. official messages) so it wraps
   * like the mobile card instead of clipping to one line.
   */
  descriptionLines?: number
  /** Footer URL shown below the description. Ignored when `cta` is set. */
  url?: string
  /**
   * Ink for both chin lines, from `useChinPalette` — fixed per surface (white on
   * the dark stop, near-black on the pale stop and the audio strip). Replaces the
   * variant's own `text-*` classes (including their opacity) on both lines, so a
   * card whose surface is not the variant default still reads.
   */
  chinTextColor?: string
  /**
   * Optional 16x16 brand badge rendered before the title (used by Link Apps:
   * Spotify, TikTok, FAQ, Form, etc.).
   */
  appIcon?: React.ReactNode
  /** Optional CTA rendered in place of the URL footer. */
  cta?: LinkAttachmentCta
  /** Trailing action rendered on the right of the title/description block. */
  trailingAction?: React.ReactNode
  /** Renders two neutral pulsing bars in place of the text. */
  isLoading?: boolean
}

const TITLE_CLASS_BY_VARIANT: Record<LinkAttachmentVariant, string> = {
  dark: 'text-white',
  light: 'text-black/90',
}

const TITLE_DIMMED_CLASS = 'text-white/30'

const SECONDARY_CLASS_BY_VARIANT: Record<LinkAttachmentVariant, string> = {
  dark: 'text-white/55',
  light: 'text-black/55',
}

// Skeleton bars, variant-aware for the same reason the placeholder hero is: a
// fixed dark tint is invisible on the dark card's own dark fill. Values
// match `SkeletonTextLines`, the toolkit's existing skeleton bars, which also
// documents why these are explicit black/white rather than `bg-current/N`
// (Tailwind emits nothing for an opacity modifier on `current`).
const SKELETON_BAR_CLASS_BY_VARIANT: Record<LinkAttachmentVariant, string> = {
  dark: 'bg-white/25',
  light: 'bg-black/15',
}

/**
 * Body of a `LinkAttachment.*` card — the "chin". Matches the Figma
 * `content > Labels` group: 16px horizontal padding, 12px vertical padding,
 * 2px gap between the title and the URL. `shrink-0` so it keeps its natural
 * height inside the shell's fixed-height column and the hero absorbs the
 * remainder.
 *
 * Returns `null` when there's nothing to render so plain image / file
 * attachments collapse to a thumbnail-only card — unless the card is still
 * loading, when the skeleton *is* the content.
 */
const CardBody: React.FC<CardBodyProps> = ({
  variant,
  ctaSurface,
  chinTextColor,
  title,
  placeholderTitle,
  description,
  descriptionLines = 1,
  url,
  appIcon,
  cta,
  trailingAction,
  isLoading = false,
}) => {
  const isDark = variant === 'dark'
  const displayTitle = title ?? (isDark ? placeholderTitle : undefined) ?? ''
  const hasTitle = displayTitle.trim() !== ''
  const hasDescription = description != null && description.trim() !== ''
  // Mirror the trimming applied by `ReceivedCard` so a whitespace-only
  // `url` collapses the body footer (and the whole body, for media-only
  // cards) instead of rendering an empty line.
  const trimmedUrl = typeof url === 'string' ? url.trim() : ''
  const hasUrl = trimmedUrl !== ''
  const hasCta = cta != null

  if (!isLoading && !hasTitle && !hasDescription && !hasUrl && !hasCta) {
    return null
  }

  const titleDimmed = isDark && !title

  const titleClass = classNames(
    'line-clamp-2 text-[14px] font-medium leading-5 tracking-[0.28px]',
    chinTextColor == null &&
      (titleDimmed ? TITLE_DIMMED_CLASS : TITLE_CLASS_BY_VARIANT[variant])
  )

  const secondaryClass = classNames(
    'truncate text-[12px] leading-4 tracking-[0.24px]',
    chinTextColor == null && SECONDARY_CLASS_BY_VARIANT[variant]
  )

  // Single-line `truncate` at the default; >1 clamps via `descriptionStyle` below. The URL footer
  // keeps `secondaryClass` so it always stays single-line.
  const descriptionClass = classNames(
    'text-[12px] leading-4 tracking-[0.24px]',
    descriptionLines <= 1 && 'truncate',
    chinTextColor == null && SECONDARY_CLASS_BY_VARIANT[variant]
  )

  if (isLoading) {
    const barClass = classNames(
      'animate-pulse rounded',
      SKELETON_BAR_CLASS_BY_VARIANT[variant]
    )

    return (
      <div className="shrink-0 px-4 py-3">
        <div className="flex min-w-0 flex-col gap-[2px]">
          {/* Sized to the lines they stand in for: 14/20 title, 12/16 URL. */}
          <div className={classNames('h-5 w-3/4', barClass)} aria-hidden />
          <div className={classNames('h-4 w-1/2', barClass)} aria-hidden />
        </div>
      </div>
    )
  }

  const chinTextStyle = chinTextColor ? { color: chinTextColor } : undefined
  const descriptionStyle: React.CSSProperties | undefined =
    descriptionLines > 1
      ? {
          ...chinTextStyle,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: descriptionLines,
          overflow: 'hidden',
        }
      : chinTextStyle

  return (
    <div className="shrink-0 px-4 py-3">
      <div className="flex items-end gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
          {hasTitle && (
            <div className="flex min-w-0 items-center gap-2">
              {appIcon ? <span className="shrink-0">{appIcon}</span> : null}
              <p
                className={classNames('min-w-0', titleClass)}
                style={chinTextStyle}
              >
                {displayTitle}
              </p>
            </div>
          )}

          {hasDescription && (
            <p className={descriptionClass} style={descriptionStyle}>
              {description}
            </p>
          )}

          {!hasCta && hasUrl && (
            <p
              className={secondaryClass}
              style={chinTextStyle}
              data-testid="link-attachment-url"
            >
              {trimmedUrl}
            </p>
          )}
        </div>

        {trailingAction && <div className="shrink-0">{trailingAction}</div>}
      </div>

      {cta && <CardCta variant={ctaSurface ?? variant} cta={cta} />}
    </div>
  )
}

export default CardBody
